import React, {
    FunctionComponent,
    memo,
    RefObject,
    Suspense,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import styles from './Chat.module.sass'
import Button from '../../../../controls/Button/Button'
import clsx from 'clsx'
import { ID } from '../../../../../types/UtilityTypes'
import {
    useAnyPermissions,
    useRootDispatch,
    useRootSelector,
    useStayScrolled,
    useUpload
} from '../../../../../utils/hooks'
import { SortDirection } from '../../../../../constants/direction'
import { pick, toggle } from '../../../../../utils/utils'
import { RequestStatus } from '../../../../../responses/response'
import { HistoryFilterType } from '../../../../../responses/history'
import { eqString } from 'fp-ts/es6/Eq'
import DirectionPicker from './DirectionPicker'
import { identity, pipe } from 'fp-ts/es6/function'
import CommentIcon from 'files/comment/comment.svg'
import ReadAllIcon from 'files/comment/read-all.svg'
//import ImageIcon from 'files/image.svg'
import { useParams } from 'react-router'
import { reverse } from 'fp-ts/es6/Array'
import { historyActions as actions, historyActions } from '../../../../../redux/global/history/history-actions'
import { PERMISSIONS } from '../../../../../constants/permissions'
import { isTempFileData } from '../../../../../responses/files'
import QuillEditor from '../../../../etc/Editor/QuillEditor'
import HistoryElement from '../../../../data-components/elements/HistoryElement/HistoryElement'
import { Colors } from '../../../../../styles'
import { CommentEditState } from '../../../../../redux/global/history/history-reducer'
import { not } from 'fp-ts/function'
import { inactiveContext } from 'ts/constants/contexts'

export const ChatHeader: FunctionComponent<{ className?: string }> = memo(props => {
    const dispatch = useRootDispatch()
    const filters = useRootSelector(state => state.global.history.filters)
    const changeFilterType = useCallback((type: HistoryFilterType) => {
        dispatch(actions.fetch.request({
            ...filters,
            filterType: toggle<HistoryFilterType>(eqString)(type)(filters.filterType)
        }))
    }, [dispatch, filters])

    const { taskId } = useParams<{ taskId: string }>()
    const unread = useRootSelector(state => state.global.notification.tasks[taskId] ?? 0)

    return <inactiveContext.Provider value={false}>
        <div className={clsx(props.className, styles.header)}>
            <div className={styles.content}>
                <DirectionPicker/>
                <div className={styles.title}>Комментарии</div>
                {/*<Button
                    title={'Фильтр по файлам'}
                    color={filters.filterType?.includes('files') ? Colors.blue : Colors.grey}
                    onClick={() => changeFilterType('files')}
                >
                    <ImageIcon/>
                </Button>*/}
                <Button
                    title={'Показывать только комментарии'}
                    color={filters.filterType?.includes('comments') ? Colors.blue : Colors.grey}
                    onClick={() => changeFilterType('comments')}
                >
                    <CommentIcon/>
                </Button>
                <Button
                    title={'Прочитать все'}
                    color={unread > 0 ? Colors.orange : Colors.grey}
                    onClick={() => dispatch(historyActions.readAll.request())}
                >
                    <ReadAllIcon/>
                </Button>
            </div>
        </div>
    </inactiveContext.Provider>
})

ChatHeader.displayName = 'ChatHeader'

const ChatBody = memo<{ className?: string, scrollRef?: RefObject<HTMLElement> }>(props => {
    const dispatch = useRootDispatch()
    const ref = useRef<HTMLDivElement>(null)
    const { data, resources } = useRootSelector(state => pick(state.global.history, 'data', 'resources'))
    const direction = useRootSelector(state => state.global.user.data?.settings.history_direction ?? SortDirection.DESC)
    const isReversed = direction === SortDirection.DESC
    const { stayScrolled } = useStayScrolled(props.scrollRef ?? ref, { reversed: isReversed })
    const preparedData = useMemo(() => pipe(data, isReversed ? reverse : identity), [data, isReversed])

    useLayoutEffect(() => {
        stayScrolled()
    }, [data.length, stayScrolled])

    const onRead = useCallback((id: ID) => dispatch(historyActions.read.request(id)), [dispatch])

    return <div ref={ref} className={clsx(props.className, styles.body)}>
        <div className={styles.messages}>
            {
                preparedData.map(data => <HistoryElement
                    key={data.id}
                    data={data}
                    resources={resources}
                    onRead={onRead}
                />)
            }
        </div>
    </div>
})

export { ChatBody }

interface EditorProps {
    className?: string,
}

export const ChatEditor: FunctionComponent<EditorProps> = memo(props => {
    const dispatch = useRootDispatch()
    const { editState, sendStatus, filters, editor, quote } = useRootSelector(state =>
        pick(state.global.history, 'editState', 'sendStatus', 'filters', 'editor', 'quote')
    )
    const [text, setText] = useState('')
    const [resulted, setResulted] = useState(false)

    const upload = useUpload()
    const clearFiles = upload.clear

    const autoResult = useAnyPermissions([PERMISSIONS.COMMENT.AUTO_RESULT])

    const clear = useCallback(() => {
        setText('')
        setResulted(false)
        clearFiles()
    }, [clearFiles])

    useEffect(() => clear(), [clear, editor])

    useEffect(() => {
        if (upload.files.filter(isTempFileData).length > 0 && autoResult) {
            setResulted(true)
        }
    }, [upload.files, autoResult])

    return <Suspense fallback={<div className={styles.editor} style={{ height: 200 }}/>}>
        {
            not(CommentEditState.is.update)(editState) && <div className={clsx(props.className, styles.editor)}>
                <QuillEditor
                    className={styles.inner}
                    placeholder={'Добавить комментарий'}
                    loading={sendStatus === RequestStatus.FETCHING}
                    value={text}
                    onChange={setText}
                    resulted={resulted}
                    onResultedChange={setResulted}
                    upload={upload}
                    onSend={data => dispatch(actions.comment.request({ filters, data }))}
                    quote={quote}
                    onQuoteDelete={() => dispatch(historyActions.quote(null))}
                />
            </div>
        }
    </Suspense>
})

ChatEditor.displayName = 'ChatEditor'